import { createFileRoute, Navigate } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";
import { FieldGroup, Fieldset } from "src/components/base/fieldset";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiPublic } from "src/api/api_public";
import { Text } from "src/components/base/text";
import { PENDING_LOGIN, PREFERRED_LOGIN } from "src/utils/local-storage";

/**
 * The properties for {@link TailscaleLogin}
 */
export type TailscaleLoginProps = {};

/**
 * Login component, when a user logs in via tailscale cli
 */
export default function TailscaleLogin(props: TailscaleLoginProps) {
    const [t] = useTranslation("static");

    const preferredLogin = PREFERRED_LOGIN.get();

    const { token } = Route.useParams();
    const data = Route.useLoaderData();

    // Deprecated method, redirect to only tenant
    if (preferredLogin === "Oidc" && data.list.length === 1) {
        const { uuid } = data.list[0];
        PENDING_LOGIN.set(`Oidc-${uuid}`);
        window.location.href = `/api/frontend/v1/oidc/begin-vpn-login/${uuid}/${token}`;
        return null;
    }
    if (preferredLogin?.startsWith("Oidc-")) {
        const uuid = preferredLogin.slice("Oidc-".length);
        window.location.href = `/api/frontend/v1/oidc/begin-vpn-login/${uuid}/${token}`;
        return null;
    }
    if (preferredLogin === "External") {
        return <Navigate to={"/u/tailscale-login/$token"} params={{ token }} />;
    }

    return (
        <div className={"flex flex-col items-center justify-center gap-4"}>
            <div className="w-full max-w-md rounded-xl border bg-white dark:border-zinc-800 dark:bg-zinc-900 dark:before:pointer-events-none forced-colors:outline">
                <div
                    className={
                        "grid h-full w-full place-items-start justify-items-center overflow-hidden p-6 py-8 sm:p-8 lg:p-12"
                    }
                >
                    <div className={"flex w-full max-w-sm space-y-8 overflow-hidden p-1"}>
                        <Fieldset className={"w-full"}>
                            <FieldGroup>
                                <div className={"flex flex-col gap-4"}>
                                    {data.list.map((x) => (
                                        <PrimaryButton
                                            className={"w-full"}
                                            href={`/api/frontend/v1/oidc/begin-vpn-login/${x.uuid}/${token}`}
                                            onClick={() => PENDING_LOGIN.set(`Oidc-${x.uuid}`)}
                                            external={true}
                                        >
                                            {x.name}
                                        </PrimaryButton>
                                    ))}
                                    {data.list.length === 0 && (
                                        <Text className={"text-center"}>{t("description.no-login-provider")}</Text>
                                    )}
                                    <Button
                                        outline={true}
                                        className={"w-full"}
                                        href={"/u/tailscale-login/$token"}
                                        params={{ token }}
                                        onClick={() => PENDING_LOGIN.set("External")}
                                    >
                                        {t("button.sign-in-with-external-login")}
                                    </Button>
                                </div>
                            </FieldGroup>
                        </Fieldset>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_static/s/login/$token/")({
    component: TailscaleLogin,
    loader: async () => await ApiPublic.vpnAuth.getLogins(),
});
